import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { generateMonthOptions, parseMonthValue } from '../utils/dateUtils';

const useExpensesData = ({ expensesUrl, getAuthHeaders, accountId, showSuccessMessage }) => {
  const [expenses, setExpenses] = useState([]);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const [expensesError, setExpensesError] = useState('');

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);

  const fetchExpenses = useCallback(async () => {
    setIsLoadingExpenses(true);
    setExpensesError('');
    try {
      const config = await getAuthHeaders();
      const monthParams = parseMonthValue(selectedMonth);
      const params = monthParams ? { year: monthParams.year, month: monthParams.month } : {};
      const response = await axios.get(expensesUrl, { ...config, params });
      if (response.data.success) {
        setExpenses(response.data.data.expenses || []);
      } else {
        setExpensesError(response.data.error || 'Failed to fetch expenses');
      }
    } catch (err) {
      console.error('Error fetching expenses:', err);
      setExpensesError('Failed to load expenses');
    } finally {
      setIsLoadingExpenses(false);
    }
  }, [expensesUrl, getAuthHeaders, selectedMonth]);

  const createExpense = async (formData) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(expensesUrl, { ...formData, accountId }, config);
      if (response.data.success) {
        setExpenses(prev => [response.data.data, ...prev]);
        return true;
      } else {
        setExpensesError(response.data.error || 'Failed to save expense');
        return false;
      }
    } catch (err) {
      console.error('Error creating expense:', err);
      setExpensesError(err.response?.data?.error || 'Failed to save expense');
      return false;
    }
  };

  const updateExpense = async (expenseId, formData) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.put(`${expensesUrl}/${expenseId}`, { ...formData, accountId }, config);
      if (response.data.success) {
        setExpenses(prev =>
          prev.map(exp => exp.id === expenseId ? response.data.data : exp)
        );
        return true;
      } else {
        setExpensesError(response.data.error || 'Failed to save expense');
        return false;
      }
    } catch (err) {
      console.error('Error updating expense:', err);
      setExpensesError(err.response?.data?.error || 'Failed to save expense');
      return false;
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.delete(`${expensesUrl}/${expenseId}`, config);
      if (response.data.success) {
        setExpenses(prev => prev.filter(exp => exp.id !== expenseId));
        showSuccessMessage('Expense deleted successfully!');
        return true;
      } else {
        setExpensesError(response.data.error || 'Failed to delete expense');
        return false;
      }
    } catch (err) {
      console.error('Error deleting expense:', err);
      setExpensesError(err.response?.data?.error || 'Failed to delete expense');
      return false;
    }
  };

  return {
    expenses,
    isLoadingExpenses,
    expensesError,
    setExpensesError,
    fetchExpenses,
    createExpense,
    updateExpense,
    deleteExpense,
    selectedMonth,
    setSelectedMonth,
    monthOptions,
  };
};

export default useExpensesData;
